import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import './ReviewerProgressWidget.scss';
import ArrWidgetWrapper from '../../../common/arr-widget-wrapper/ArrWidgetWrapper';
import {t} from 'i18next';
import ArrPieChart, {
  ArrPieChartData,
} from '../../../common/arr-piechart/ArrPieChart';
import {ReviewerProgress} from '../../../../shared/types';
import {TABLE_KEYS} from '../../../../shared/constants';
import {useReviewProgress} from '../../../../hooks/dashboard/useDashboard';
import {CustomizeReviewerProgressWidget} from '../customize-reviewer-progress-widget/CustomizeReviewerProgressWidget';
import {
  ReviewerProgressData,
  ReviewerProgressFilterDataRPW,
} from '../../dashboardTypes';
import {widgetIdMapping} from '../../dashboardUtils';

export const columns: ReviewerProgress[] = [
  {key: TABLE_KEYS.name, label: 'Name'},
  {key: TABLE_KEYS.distributed, label: 'Distributed'},
  {key: TABLE_KEYS.completed, label: 'Completed'},
  {key: TABLE_KEYS.todo, label: 'To Do'},
];

interface Reviewer {
  name: string;
  distributed: number;
  completed: number;
  todo: number;
}

interface ReviewProgressProps {
  showSettingsIcon?: boolean;
  onReplaceClick: () => void;
  reviewProgressConfig?: ReviewerProgressFilterDataRPW;
}
const ReviewerProgressWidget: React.FC<ReviewProgressProps> = ({
  onReplaceClick,
  showSettingsIcon = false,
  reviewProgressConfig = {},
}) => {
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const handleDrawer = (value: React.SetStateAction<boolean>) =>
    setIsCustomizeOpen(value);
  const [data, setData] = useState<ReviewerProgressData>();
  const {
    data: reviewData,
    isLoading: loading,
    isError: error,
  } = useReviewProgress({
    ...reviewProgressConfig,
    isDataUpdate: false,
    widgetId: widgetIdMapping.reviewerProgress,
  });

  useEffect(() => {
    setData(reviewData);
  }, [reviewData]);

  const onUpdateConfig = (newData: ReviewerProgressData) => {
    setData(newData);
  };

  const completed = data?.chart[0].data ?? 0;
  const toDo = data?.chart[1].data ?? 0;

  const displayedData = (data?.dashboardList ?? []).map((reviewer) => ({
    name: reviewer.reveiwerName,
    distributed: reviewer.totalDocs,
    completed: reviewer.completed,
    todo: reviewer.remainDocs,
  }));

  const pieChartData = [
    {
      id: '1',
      value: completed,
      label: 'Completed',
      color: '#7DDFBB',
    },
    {
      id: '2',
      value: toDo,
      label: 'To Do',
      color: '#FFC37D',
    },
  ] as ArrPieChartData[];

  const reviewerProgressFilterData: ReviewerProgressFilterDataRPW = {
    completed: data?.reviewerProgressFilterData?.completed ?? 0,
    inProgressDocs: data?.reviewerProgressFilterData?.inProgressDocs ?? 0,
    remainDocs: data?.reviewerProgressFilterData?.remainDocs ?? 0,
    totalDocs: data?.reviewerProgressFilterData?.totalDocs ?? 0,
    reveiwerName: data?.reviewerProgressFilterData?.reveiwerName ?? '',
    assignments: data?.reviewerProgressFilterData?.assignments ?? [],
    reviewers: data?.reviewerProgressFilterData?.reviewers ?? [],
    reviewersName: data?.reviewerProgressFilterData?.reviewersName ?? '',
    isCompleted: data?.reviewerProgressFilterData?.isCompleted ?? 0,
    percentage: data?.reviewerProgressFilterData?.percentage ?? 0,
    widgetId: data?.reviewerProgressFilterData?.widgetId ?? 0,
    isDataUpdate: data?.reviewerProgressFilterData?.isDataUpdate ?? false,
  };

  const reviewerTitle = useMemo(() => {
    if (data) {
      const {
        reviewerProgressFilterData: {isCompleted, reviewers},
      } = data as ReviewerProgressData;
      if (reviewers && reviewers?.length > 0) {
        return 'dashboard.reviewerWidget.reviewerTitleSpecific';
      } else
        return isCompleted === 1
          ? 'dashboard.reviewerWidget.reviewerTitleCompleted'
          : 'dashboard.reviewerWidget.reviewerTitle';
    }
    return '';
  }, [JSON.stringify(data)]);

  return (
    <ArrWidgetWrapper
      title={t('dashboard.reviewerWidget.widgetTitle')}
      showInfo
      showReplace
      showSettings={showSettingsIcon}
      onReplaceClick={onReplaceClick}
      onSettingsClick={() => handleDrawer(true)}
      loading={loading}
      error={error}
      tooltipContent={t('dashboard.reviewerWidget.tooltip')}
    >
      <CustomizeReviewerProgressWidget
        showDrawer={isCustomizeOpen}
        handleDrawer={handleDrawer}
        initialData={reviewerProgressFilterData}
        onSuccess={onUpdateConfig}
      />
      <Box className="reviewer-progress-root" data-testid="reviewer-progress">
        <ArrPieChart
          title="Total distributed: "
          data={pieChartData}
          total={completed + toDo}
        />
        <Box className="reviewer-table">
          <Typography
            className="typo-text subhead5_size"
            data-testid={`reviewer-progress-title`}
          >
            {t(reviewerTitle)}
          </Typography>
          <Table className="table-container">
            <TableHead>
              <TableRow>
                {columns.map((header) => (
                  <TableCell
                    key={header.label}
                    className="reviewer-table-header"
                    sx={{width: TABLE_KEYS.name === header.key ? '30%' : '20%'}}
                    data-testid={`reviewer-progress-table-header-${header.key}`}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedData.map((r, idx) => (
                <TableRow key={idx.toLocaleString()}>
                  {columns.map((col) => {
                    const value = r[col.key as keyof Reviewer];
                    let styleForColumn = 'table-row';
                    if (col.key === TABLE_KEYS.distributed) {
                      styleForColumn = 'table-column-distributed';
                    } else if (col.key === TABLE_KEYS.completed) {
                      styleForColumn = 'table-column-completed';
                    } else if (col.key === TABLE_KEYS.todo) {
                      styleForColumn = 'table-column-todo';
                    }
                    return (
                      <TableCell
                        key={col.key}
                        className={`table-row ${styleForColumn}`}
                        data-testid={`reviewer-progress-table-row-${col.key}-${idx}-value`}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </ArrWidgetWrapper>
  );
};

export default ReviewerProgressWidget;
